import React, { useState, useEffect } from 'react';
import { API_URL, apiFetch } from '../services/api';
import { ContactFormModal } from './ContactFormModal';
import { PhoneIcon, BriefcaseIcon, UserIcon, ArrowRightIcon } from './icons';

// ScrollReveal Hook - triggers animation when element enters viewport
const useScrollReveal = (options?: { threshold?: number; rootMargin?: string }) => {
    const [ref, setRef] = useState<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!ref) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(ref);
                }
            },
            { threshold: options?.threshold || 0.15, rootMargin: options?.rootMargin || '0px' }
        );
        observer.observe(ref);
        return () => observer.disconnect();
    }, [ref, options?.threshold, options?.rootMargin]);

    return { ref: setRef, isVisible };
};

// ScrollReveal wrapper component
const ScrollReveal: React.FC<{
    children: React.ReactNode;
    delay?: number;
    direction?: 'up' | 'down' | 'left' | 'right' | 'none';
    duration?: number;
    className?: string;
}> = ({ children, delay = 0, direction = 'up', duration = 700, className = '' }) => {
    const { ref, isVisible } = useScrollReveal();

    const directionStyles: Record<string, string> = {
        up: 'translate-y-12',
        down: '-translate-y-12',
        left: 'translate-x-12',
        right: '-translate-x-12',
        none: ''
    };

    return (
        <div
            ref={ref}
            className={`transition-all ease-out ${isVisible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${directionStyles[direction]}`} ${className}`}
            style={{ transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

interface LandingPageProps {
    onLoginClick: () => void;
    onPlanClick?: (packageId?: string) => void;
    onEduAnalizClick: () => void;
    previewContent?: LandingContent; // Önizleme modu için dışarıdan içerik
}

interface LandingContent {
    header: {
        logoUrl: string;
        companyName: string;
        menuLinks: { label: string; href: string }[];
        loginButtonText: string;
        ctaButtonText: string;
    };
    hero: {
        badgeText: string;
        title: string;
        subtitle: string;
        description: string;
        backgroundType: 'gradient' | 'image' | 'video';
        backgroundImage: string;
        backgroundVideo: string;
        ctaPrimaryText: string;
        ctaSecondaryText: string;
    };
    gradeCards: {
        card1: { icon: string; title: string; description: string };
        card2: { icon: string; title: string; description: string };
    };
    keyFeatures: string[];
    marqueeTexts: string[];
    slider: Array<{
        imageUrl: string;
        title: string;
        subtitle: string;
        buttonText: string;
        buttonLink: string;
        order: number;
    }>;
    features: Array<{
        icon: string;
        title: string;
        description: string;
        color: string;
        imageUrl?: string;
        isActive: boolean;
    }>;
    aboutSupervisor: {
        image: string;
        title: string;
        description: string;
        points: Array<{ number: string; title: string; description: string }>;
    };
    about?: any; // Legacy field
    visionMission: {
        mission: { icon: string; title: string; description: string };
        vision: { icon: string; title: string; description: string };
    };
    stats: Array<{
        value: string;
        suffix: string;
        label: string;
        color: string;
    }>;
    cta: {
        title: string;
        description: string;
        primaryButtonText: string;
        secondaryButtonText: string;
    };
    footer: {
        companyName: string;
        description: string;
        contactEmail: string;
        contactPhone: string;
        address: string;
        socialLinks: {
            facebook: string;
            twitter: string;
            instagram: string;
            linkedin: string;
            youtube: string;
        };
    };
    pricing: {
        title: string;
        subtitle: string;
        packages: {
            id: string;
            name: string;
            price: string;
            period: string;
            description: string;
            features: string[];
            isPopular: boolean;
            ctaText: string;
            color: string;
            isActive: boolean;
            durationDays: number;
        }[];
        billingCycles?: {
            monthly: { label: string; multiplier: number; periodLabel: string; badge: string };
            sixMonths: { label: string; multiplier: number; periodLabel: string; badge: string };
            untilExam: { label: string; multiplier: number; periodLabel: string; badge: string };
        };
    };
}

// Animated Counter Component
const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({
    end,
    suffix = '',
    duration = 2000
}) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById('stats-section');
        if (element) observer.observe(element);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        let startTime: number;
        const animate = (currentTime: number) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            setCount(Math.floor(progress * (isNaN(end) ? 0 : end)));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [isVisible, end, duration]);

    return <span>{count}{suffix}</span>;
};

const defaultContent: LandingContent = {
    header: {
        logoUrl: '/supervisor-logo-2026.png',
        companyName: 'Süpervisor Eğitim',
        menuLinks: [
            { label: 'Süpervisor Eğitim Hakkında', href: '#about-supervisor' },
            { label: 'Özellikler', href: '#features' },
            { label: 'Hakkımızda', href: '#about-us' }
        ],
        loginButtonText: 'Giriş',
        ctaButtonText: 'Sizi Arayalım'
    },
    hero: {
        badgeText: '',
        title: 'Bütüncül Yaklaşımla',
        subtitle: 'Sürekli Gelişim',
        description: 'Akademik gelişimden kariyer planlamasına, alışkanlık kazandırmadan kişisel gelişime seni tanıyan, sana özel yol haritası çizen profesyonel bir ekosistem.',
        backgroundType: 'gradient',
        backgroundImage: '',
        backgroundVideo: '',
        ctaPrimaryText: 'Sizi Arayalım',
        ctaSecondaryText: 'Platformu Keşfet'
    },
    gradeCards: {
        card1: { icon: '', title: '', description: '' },
        card2: { icon: '', title: '', description: '' }
    },
    keyFeatures: ['Online Birebir Dersler', 'Haftalık Danışman Görüşmeleri', 'AI Destekli Analiz'],
    features: [],
    marqueeTexts: ['TYT - AYT', 'LGS HAZIRLIK', 'EĞİTİM KOÇLUĞU', 'TERCİH DANIŞMANLIĞI', 'YAPAY ZEKA'],
    slider: [
        {
            imageUrl: '/landing-teacher.jpg.jpeg',
            title: 'Bütüncül Yaklaşımla',
            subtitle: 'Sürekli Gelişim',
            buttonText: 'Hemen Keşfet',
            buttonLink: '#features',
            order: 0
        }
    ],
    aboutSupervisor: {
        image: '/landing-student.jpg.jpeg',
        title: 'Eğitimde Yeni Bir Dönem',
        description: 'Süpervisor Eğitim, sadece bir ders çalışma platformu değil, öğrencinin akademik ve kişisel yolculuğunda ona eşlik eden akıllı bir yol arkadaşıdır.',
        points: [
            { number: '01', title: 'Bütüncül Yaklaşım', description: 'Akademik başarıyı sosyal ve kişisel gelişimle birleştiriyoruz.' },
            { number: '02', title: 'Veri Odaklı Takip', description: 'Her adımı verilerle analiz edip en doğru yönlendirmeyi yapıyoruz.' }
        ]
    },
    visionMission: {
        mission: { icon: '🎯', title: 'Misyonumuz', description: 'Her öğrencinin potansiyelini keşfetmesine ve gerçekleştirmesine yardımcı olmak. Bireyselleştirilmiş eğitim deneyimi ile akademik ve kariyer hedeflerine ulaşmalarını sağlamak, fırsat eşitliği yaratarak kaliteli eğitime erişimi kolaylaştırmak.' },
        vision: { icon: '🚀', title: 'Vizyonumuz', description: "Türkiye'nin en kapsamlı ve yenilikçi eğitim teknolojisi platformu olarak, yapay zeka destekli kişiselleştirilmiş öğrenme deneyimi sunarak eğitimde standartları yükseltmek ve global ölçekte başarı hikayeleri oluşturmak." }
    },
    stats: [
        { value: '1000', suffix: 'k+', label: 'Aktif Öğrenci', color: 'from-blue-400 to-cyan-400' },
        { value: '100', suffix: '+', label: 'Uzman Eğitim Danışmanı', color: 'from-purple-400 to-pink-400' },
        { value: '350', suffix: '+', label: 'Uzman Öğretmen', color: 'from-red-400 to-orange-400' },
        { value: '86', suffix: '%', label: 'Akademik Başarıda Artış', color: 'from-emerald-400 to-teal-400' },
        { value: '98', suffix: '%', label: 'Öğrenci Memnuniyeti', color: 'from-orange-400 to-amber-400' }
    ],
    cta: {
        title: 'Geleceğini Şansa Bırakma, Supervisor ile Planla',
        description: 'Hemen ücretsiz deneme hesabınızı oluşturun, yapay zeka destekli eğitim koçunuzla tanışın.',
        primaryButtonText: 'Ücretsiz Başla',
        secondaryButtonText: 'Danışman İle Görüş'
    },
    footer: {
        companyName: 'Süpervisor Eğitim',
        description: "Türkiye'nin en gelişmiş yapay zeka destekli eğitim ve koçluk platformu.",
        contactEmail: '',
        contactPhone: '',
        address: '',
        socialLinks: { facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '' }
    },
    pricing: {
        title: 'Başarıya Giden Yolda Size Uygun Paketi Seçin',
        subtitle: 'İhtiyaçlarınıza göre özelleştirilmiş eğitim çözümlerimizle hedeflerinize daha hızlı ulaşın.',
        packages: [
            {
                id: 'rehberlik',
                name: 'Sadece Eğitim Danışmanlığı',
                price: '1.000',
                period: '/aylık',
                description: 'Profesyonel PDR desteği ile hedeflerinize emin adımlarla ilerleyin.',
                features: [
                    'Haftalık Birebir Görüşme',
                    'Kişiye Özel Çalışma Programı',
                    'Performans ve Net Takibi',
                    'Motivasyon ve Strateji Desteği'
                ],
                isPopular: false,
                ctaText: 'Paketi Satın Al',
                color: 'from-green-500 to-green-600',
                isActive: true,
                durationDays: 30
            },
            {
                id: 'ozel-ders',
                name: 'Özel Ders + Danışmanlık',
                price: '2.450',
                period: '/aylık',
                description: 'Hem konu eksiklerini kapatın hem de rehberlik alın.',
                features: [
                    'Haftalık 2 Saat Birebir Özel Ders',
                    'Tüm Danışmanlık Hizmetleri',
                    'Ödev Takibi ve Kontrolü',
                    '7/24 Soru Sorma İmkanı'
                ],
                isPopular: true,
                ctaText: 'Paketi Satın Al',
                color: 'from-green-500 to-green-600',
                isActive: true,
                durationDays: 30
            },
            {
                id: 'full-paket',
                name: 'FULL PAKET',
                price: '4.900',
                period: '/aylık',
                description: 'Eksiksiz bir hazırlık süreci için her şey dahil çözüm.',
                features: [
                    'Özel Ders + CANLI DERSLER',
                    'EĞİTİM KOÇLUĞU (Haftalık)',
                    'SORU ÇÖZÜM PAKETİ (Sınırsız)',
                    'Deneme Sınavları ve Analizler',
                    'Veli Bilgilendirme Sistemi'
                ],
                isPopular: false,
                ctaText: 'Paketi Satın Al',
                color: 'from-green-500 to-green-600',
                isActive: true,
                durationDays: 30
            }
        ],
        billingCycles: {
            monthly: { label: 'Aylık', multiplier: 1, periodLabel: '/ aylık', badge: '' },
            sixMonths: { label: '6 Aylık', multiplier: 5, periodLabel: '/ 6 Ay', badge: '%15 İndirim' },
            untilExam: { label: 'Sınava Kadar', multiplier: 8, periodLabel: '/ Sınava Kadar', badge: 'En Avantajlı' }
        }
    }
};

export const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick, onPlanClick, onEduAnalizClick, previewContent }) => {
    const [content, setContent] = useState<LandingContent>(defaultContent);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [billingCycle, setBillingCycle] = useState<'monthly' | '6months' | 'untilExam'>('monthly');
    const [isReady, setIsReady] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [selectedGrade, setSelectedGrade] = useState('');
    const [scrollY, setScrollY] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    // Close mobile menu on resize to desktop (md: 768px)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Smooth scroll helper for menu links
    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        if (href.startsWith('#')) {
            e.preventDefault();
            setIsMobileMenuOpen(false);
            const target = document.querySelector(href);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    // Track scroll position for Parallax effect
    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-play for slider
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % content.slider.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [content.slider.length]);

    // Fetch landing page content from API
    useEffect(() => {
        if (previewContent) {
            setContent(previewContent);
            return;
        }

        const loadContent = async () => {
            try {
                const response = await apiFetch(`${API_URL}/api/landing-content`);
                if (response.ok) {
                    const data = await response.json();
                    // Secure deep merge with defaults to prevent crashes on missing properties
                    // "Hemen Başla" metnini "Sizi Arayalım" ile otomatik değiştir
                    const cleanHeaderCta = (text: string) => {
                        if (!text) return 'Sizi Arayalım';
                        const cleaned = text.trim();
                        if (cleaned === 'Hemen Başla' || cleaned === 'Ücretsiz Başla' || cleaned === 'Paketleri İncele') return 'Sizi Arayalım';
                        return cleaned;
                    };
                    const cleanBadgeText = (text: string) => {
                        if (!text) return '';
                        const cleaned = text.trim();
                        if (cleaned === 'Yapay Zeka Destekli Eğitim Ekosistemi' || cleaned === 'Yapay Zeka Destekli Akıllı Koçluk') return '';
                        return cleaned;
                    };
                    const cleanPricingCta = (text: string) => {
                        if (!text) return 'Paketi Satın Al';
                        const cleaned = text.trim();
                        if (
                            cleaned === 'Hemen Başla' ||
                            cleaned === 'Hemen Başlat' ||
                            cleaned === 'Ücretsiz Başla' ||
                            cleaned === 'Sizi Arayalım' ||
                            cleaned === 'Paketleri İncele' ||
                            cleaned === 'Paketi İncele'
                        ) return 'Paketi Satın Al';
                        return cleaned;
                    };
                    const mergedContent = {
                        ...defaultContent,
                        ...data,
                        header: {
                            ...defaultContent.header,
                            ...(data.header || {}),
                            ctaButtonText: cleanHeaderCta(data.header?.ctaButtonText || defaultContent.header.ctaButtonText)
                        },
                        hero: {
                            ...defaultContent.hero,
                            ...(data.hero || {}),
                            badgeText: cleanBadgeText(data.hero?.badgeText || ''),
                            ctaPrimaryText: cleanHeaderCta(data.hero?.ctaPrimaryText || defaultContent.hero.ctaPrimaryText)
                        },
                        gradeCards: { ...defaultContent.gradeCards, ...(data.gradeCards || {}) },
                        aboutSupervisor: {
                            ...defaultContent.aboutSupervisor,
                            ...(data.aboutSupervisor || {}),
                            // Force local student image regardless of database
                            image: '/landing-student.jpg.jpeg',
                            points: data.aboutSupervisor?.points || defaultContent.aboutSupervisor.points
                        },
                        marqueeTexts: data.marqueeTexts || defaultContent.marqueeTexts,
                        // Force teacher image in the first slider item
                        slider: (data.slider && data.slider.length > 0 ? data.slider : defaultContent.slider).map((s: any) => ({
                            ...s,
                            buttonText: cleanHeaderCta(s.buttonText)
                        })),
                        visionMission: { ...defaultContent.visionMission, ...(data.visionMission || {}) },
                        cta: {
                            ...defaultContent.cta,
                            ...(data.cta || {}),
                            primaryButtonText: cleanHeaderCta(data.cta?.primaryButtonText || defaultContent.cta.primaryButtonText)
                        },
                        footer: {
                            ...defaultContent.footer,
                            ...(data.footer || {}),
                            socialLinks: { ...defaultContent.footer.socialLinks, ...(data.footer?.socialLinks || {}) }
                        },
                        // Explicitly merge pricing to ensure backend data is used
                        pricing: data.pricing ? {
                            ...defaultContent.pricing,
                            ...data.pricing,
                            packages: (data.pricing.packages || defaultContent.pricing.packages).map((p: any) => ({
                                ...p,
                                ctaText: cleanPricingCta(p.ctaText)
                            })),
                            billingCycles: data.pricing.billingCycles || defaultContent.pricing.billingCycles
                        } : defaultContent.pricing
                    };
                    setContent(mergedContent);
                }
            } catch (error) {
                console.error('Error loading landing content:', error);
            } finally {
                setTimeout(() => setIsReady(true), 100);
            }
        };
        loadContent();
    }, [previewContent]);

    // Get features - use API data if available and has items, otherwise use defaults
    const displayFeatures = (content?.features && content.features.length > 0 && content.features.some(f => f.title))
        ? content.features.filter(f => f.isActive !== false).map((f, idx) => ({
            icon: f.icon || '🎯',
            color: f.color || 'from-blue-500 to-cyan-500',
            title: f.title || '',
            desc: f.description || '',
            imageUrl: f.imageUrl || ''
        }))
        : [
            { icon: '👨‍🏫', color: 'from-[#0F2942] to-blue-700', title: 'Profesyonel Eğitim Danışmanlığı', desc: 'Alanında uzman eğitim koçları ile haftalık görüşmeler, motivasyon desteği ve akademik takip.', imageUrl: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&h=450&fit=crop' },
            { icon: '📚', color: 'from-blue-600 to-sky-500', title: 'Birebir Dersler', desc: 'Eksik olduğunuz konularda uzman öğretmenlerden özel ders ve konu anlatım desteği.', imageUrl: '/uploads/landing/birebir-ders-premium.jpg' },
            { icon: '📊', color: 'from-[#0095FF] to-blue-700', title: 'Çalışma Davranışı Analizi', desc: 'Yapay zeka ile çalışma rutinlerinizin analizi ve verimliliğinizi artıracak kişisel öneriler.', imageUrl: '/uploads/landing/calisma-analizi-premium.jpg' },
            { icon: '🧭', color: 'from-amber-500 to-orange-500', title: 'Kariyer Analizi', desc: 'İlgi ve yeteneklerinize uygun meslek seçimi, üniversite tercihi ve kariyer planlaması.', imageUrl: '/uploads/landing/kariyer-analizi-premium.jpg' },
            { icon: '💬', color: 'from-[#0F2942] to-[#0095FF]', title: '7/24 Online Destek', desc: 'Teknik sorularınız ve rehberlik ihtiyaçlarınız için her an ulaşabileceğiniz canlı destek ekibi.', imageUrl: 'https://images.unsplash.com/photo-1587560699334-cc4ff634909a?w=800&h=450&fit=crop' },
            { icon: '❓', color: 'from-blue-500 to-emerald-500', title: 'Sınırsız Soru Sorabilme', desc: 'Çözemediğiniz soruları sisteme yükleyin, uzman eğitmenler tarafından video çözümleri alın.', imageUrl: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=450&fit=crop' }
        ];

    // Get branding content with fallbacks
    const branding = {
        companyName: 'Süpervisor Eğitim',
        logoUrl: content?.header?.logoUrl || '/supervisor-logo-2026.png'
    };

    const footerContent = {
        companyName: content?.footer?.companyName || branding.companyName,
        description: content?.footer?.description || "Türkiye'nin en gelişmiş yapay zeka destekli eğitim ve koçluk platformu.",
        contactEmail: content?.footer?.contactEmail || '',
        contactPhone: content?.footer?.contactPhone || '',
        address: content?.footer?.address || '',
        socialLinks: content?.footer?.socialLinks || {
            facebook: '',
            twitter: '',
            instagram: '',
            linkedin: '',
            youtube: ''
        }
    };

    return (
        <div className={`min-h-screen bg-white transition-opacity duration-700 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
            {/* Scoped CSS for Keyframe Animations & Dot Pattern */}
            <style>{`
                @keyframes floatSlow {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes chartDraw {
                    from { stroke-dashoffset: 1000; }
                    to { stroke-dashoffset: 0; }
                }
                .animate-float-dashboard {
                    animation: floatSlow 6s ease-in-out infinite;
                }
                .chart-animated-path {
                    stroke-dasharray: 1000;
                    stroke-dashoffset: 1000;
                    animation: chartDraw 2.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                .landing-dot-pattern {
                    background-image: radial-gradient(#cbd5e1 1.2px, transparent 1.2px);
                    background-size: 28px 28px;
                }
            `}</style>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm transition-all">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <a href="#kesfet" onClick={(e) => handleNavClick(e, '#kesfet')} className="flex items-center gap-2.5 sm:gap-3 group">
                            <img
                                src={branding.logoUrl || '/supervisor-logo-2026.png'}
                                alt={branding.companyName}
                                className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-xl shadow-md object-contain border border-slate-100 group-hover:scale-105 transition-transform"
                            />
                            <div className="flex items-center gap-1">
                                <span className="text-lg sm:text-xl md:text-2xl font-extrabold text-[#0F2942] tracking-tight">
                                    Supervisor
                                </span>
                                <span className="text-lg sm:text-xl md:text-2xl font-bold text-[#0095FF]">
                                    Eğitim
                                </span>
                            </div>
                        </a>
                        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-slate-600">
                            <a href="#kesfet" onClick={(e) => handleNavClick(e, '#kesfet')} className="text-[#0095FF] font-bold hover:opacity-80 transition-colors">Keşfet</a>
                            <a href="#features" onClick={(e) => handleNavClick(e, '#features')} className="hover:text-[#0F2942] transition-colors">Özellikler</a>
                            <a href="#pricing" onClick={(e) => handleNavClick(e, '#pricing')} className="hover:text-[#0F2942] transition-colors">Paketler</a>
                            <button onClick={onEduAnalizClick} className="hover:text-[#0095FF] transition-colors text-sm font-semibold flex items-center gap-1.5">
                                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-[#0095FF] font-black text-[10px]">EA</span>
                                EduAnaliz
                            </button>
                            <a href="#grade-programs" onClick={(e) => handleNavClick(e, '#grade-programs')} className="hover:text-[#0F2942] transition-colors">Programlar</a>
                            <a href="#about-supervisor" onClick={(e) => handleNavClick(e, '#about-supervisor')} className="hover:text-[#0F2942] transition-colors">Hakkımızda</a>
                        </nav>
                    </div>

                    {/* Desktop Right Side */}
                    <div className="hidden md:flex items-center gap-3">
                        <button
                            onClick={onLoginClick}
                            className="px-4 py-2 text-sm font-bold text-[#0F2942] hover:text-[#0095FF] transition-colors"
                        >
                            Giriş Yap
                        </button>
                        <button
                            onClick={() => setIsContactModalOpen(true)}
                            className="px-5 py-2.5 bg-[#0F2942] hover:bg-[#0095FF] text-white font-bold rounded-xl shadow-md shadow-slate-900/10 hover:shadow-blue-500/20 transition-all transform hover:-translate-y-0.5 text-sm flex items-center gap-2"
                        >
                            <span>{content?.header?.ctaButtonText || 'Sizi Arayalım'}</span>
                            <PhoneIcon className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Mobile Right Side (Hamburger Menu Button + Compact Call Button) */}
                    <div className="flex md:hidden items-center gap-2">
                        <button
                            onClick={() => setIsContactModalOpen(true)}
                            className="px-3 py-2 bg-[#0F2942] active:bg-[#0095FF] text-white font-bold rounded-xl shadow-sm text-xs flex items-center gap-1.5 transition-all"
                            aria-label="Sizi Arayalım"
                        >
                            <PhoneIcon className="w-3.5 h-3.5" />
                            <span className="text-xs font-bold">{content?.header?.ctaButtonText || 'Sizi Arayalım'}</span>
                        </button>

                        {/* Hamburger Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="w-10 h-10 rounded-xl bg-slate-100/90 active:bg-slate-200/90 text-slate-800 flex flex-col items-center justify-center gap-1 transition-all border border-slate-200/90 focus:outline-none focus:ring-2 focus:ring-[#0095FF]/50"
                            aria-label={isMobileMenuOpen ? 'Menüyü Kapat' : 'Menüyü Aç'}
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span
                                className={`w-5 h-0.5 bg-[#0F2942] rounded-full transition-all duration-300 transform ${
                                    isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                                }`}
                            />
                            <span
                                className={`w-5 h-0.5 bg-[#0F2942] rounded-full transition-all duration-300 ${
                                    isMobileMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'
                                }`}
                            />
                            <span
                                className={`w-5 h-0.5 bg-[#0F2942] rounded-full transition-all duration-300 transform ${
                                    isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                                }`}
                            />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Drawer / Navigation Menu Overlay */}
            <div
                className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
                    isMobileMenuOpen ? 'visible pointer-events-auto' : 'invisible pointer-events-none'
                }`}
            >
                {/* Backdrop */}
                <div
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 ${
                        isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                />

                {/* Sliding Drawer Panel */}
                <div
                    className={`absolute top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col justify-between transition-transform duration-300 ease-out z-10 ${
                        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                >
                    {/* Drawer Top / Header */}
                    <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <img
                                src={branding.logoUrl || '/supervisor-logo-2026.png'}
                                alt={branding.companyName}
                                className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-contain shadow-sm border border-slate-100"
                            />
                            <div className="flex items-center gap-1">
                                <span className="text-base sm:text-lg font-extrabold text-[#0F2942]">Supervisor</span>
                                <span className="text-base sm:text-lg font-bold text-[#0095FF]">Eğitim</span>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors"
                            aria-label="Kapat"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Drawer Nav Links */}
                    <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-6 space-y-1.5">
                        <a
                            href="#kesfet"
                            onClick={(e) => handleNavClick(e, '#kesfet')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">🚀</span>
                                Keşfet
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>

                        <a
                            href="#features"
                            onClick={(e) => handleNavClick(e, '#features')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">⭐</span>
                                Özellikler
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>

                        <a
                            href="#pricing"
                            onClick={(e) => handleNavClick(e, '#pricing')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">💎</span>
                                Paketler & Fiyatlar
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>

                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                onEduAnalizClick();
                            }}
                            className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all text-left"
                        >
                            <span className="flex items-center gap-3">
                                <span className="w-5 h-5 rounded bg-blue-100 text-[#0095FF] font-black text-[10px] flex items-center justify-center shadow-inner">
                                    EA
                                </span>
                                EduAnaliz
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-[#0095FF] text-[10px] font-black rounded-md">Yeni</span>
                        </button>

                        <a
                            href="#grade-programs"
                            onClick={(e) => handleNavClick(e, '#grade-programs')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">🎓</span>
                                LGS & YKS Programları
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>

                        <a
                            href="#about-supervisor"
                            onClick={(e) => handleNavClick(e, '#about-supervisor')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">🏢</span>
                                Hakkımızda
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>

                        <a
                            href="#testimonials"
                            onClick={(e) => handleNavClick(e, '#testimonials')}
                            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-slate-800 font-bold hover:bg-blue-50 hover:text-[#0095FF] transition-all"
                        >
                            <span className="flex items-center gap-3">
                                <span className="text-base">💬</span>
                                Öğrenci & Veli Yorumları
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-slate-400" />
                        </a>
                    </div>

                    {/* Drawer Footer Actions */}
                    <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50/80 space-y-2.5">
                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                onLoginClick();
                            }}
                            className="w-full py-3 px-4 rounded-xl bg-white border border-slate-200 text-[#0F2942] hover:text-[#0095FF] font-bold text-xs sm:text-sm shadow-sm flex items-center justify-center gap-2 active:scale-[0.99] transition-all"
                        >
                            <UserIcon className="w-4 h-4" />
                            <span>Öğrenci / Danışman Girişi</span>
                        </button>

                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                setIsContactModalOpen(true);
                            }}
                            className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#0F2942] to-[#0095FF] text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 active:scale-[0.99] transition-all"
                        >
                            <PhoneIcon className="w-4 h-4" />
                            <span>{content?.header?.ctaButtonText || 'Sizi Arayalım (Ücretsiz Bilgi Al)'}</span>
                        </button>

                        <div className="text-center pt-1">
                            <span className="text-[10px] sm:text-[11px] font-bold text-slate-400">
                                🤖 7/24 Yapay Zeka & Mentor Desteği
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="pt-0">
                {/* Hero Section */}
                <section id="kesfet" className="pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 md:pb-16 relative overflow-hidden bg-[#F8FAFC]">
                    {/* Clean Subtle Supervisor Ambient Lighting */}
                    <div className="absolute top-10 right-1/4 w-[280px] sm:w-[500px] h-[280px] sm:h-[500px] bg-blue-100/50 rounded-full blur-3xl pointer-events-none -z-10"></div>
                    <div className="absolute top-1/3 right-5 sm:right-10 w-72 sm:w-96 h-72 sm:h-96 bg-sky-100/40 rounded-full blur-3xl pointer-events-none -z-10"></div>
                    <div className="absolute inset-0 landing-dot-pattern opacity-40 pointer-events-none -z-10"></div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-10 md:py-16">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                            
                            {/* Left Column: Typography & Action Buttons */}
                            <div className="lg:col-span-5 space-y-5 sm:space-y-6 md:space-y-7 text-center lg:text-left">
                                
                                {/* Badge */}
                                {content.hero?.badgeText ? (
                                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 sm:px-4 sm:py-2 bg-blue-50 border border-blue-200/80 rounded-full shadow-sm max-w-full">
                                        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#0095FF] rounded-full animate-pulse flex-shrink-0"></span>
                                        <span className="text-[11px] sm:text-xs font-extrabold text-[#0095FF] uppercase tracking-wider truncate">
                                            {content.hero.badgeText}
                                        </span>
                                    </div>
                                ) : null}

                                {/* Main Headline */}
                                <h1 className="text-2xl sm:text-4xl lg:text-[46px] xl:text-[50px] font-black text-[#0F2942] leading-[1.2] sm:leading-[1.15] tracking-tight">
                                    Hedeflerine Ulaşmanın En Akıllı Yolu: <span className="text-[#0095FF] block sm:inline">Supervisor</span>
                                </h1>

                                {/* Subtitle */}
                                <p className="text-xs sm:text-base lg:text-lg text-slate-600 font-medium leading-relaxed max-w-xl mx-auto lg:mx-0">
                                    {content.hero.description || 'LGS, YKS ve KPSS hazırlığında sana özel yapay zeka destekli çalışma planı, akıllı deneme analizleri ve birebir mentor takibi ile başarıyı yakala.'}
                                </p>

                                {/* CTA Action Buttons */}
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-3.5 pt-2 w-full sm:w-auto">
                                    <button
                                        onClick={() => setIsContactModalOpen(true)}
                                        className="w-full sm:w-auto px-6 py-3.5 sm:px-8 sm:py-4 bg-[#0095FF] hover:bg-[#0284C7] text-white text-sm sm:text-base font-bold rounded-xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/35 transition-all transform hover:-translate-y-0.5 active:scale-[0.98] flex items-center justify-center gap-2.5"
                                    >
                                        <span>{content.hero.ctaPrimaryText || 'Ücretsiz Başla'}</span>
                                        <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>

                                    <button
                                        onClick={onEduAnalizClick}
                                        className="w-full sm:w-auto px-5 py-3.5 sm:px-7 sm:py-4 bg-white hover:bg-slate-50 text-slate-700 text-sm sm:text-base font-bold rounded-xl border border-slate-300 shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2.5 active:scale-[0.98] group"
                                    >
                                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-50 text-[#0095FF] flex items-center justify-center group-hover:scale-110 transition-transform shadow-inner font-black text-[11px] sm:text-xs">
                                            EA
                                        </div>
                                        <span>EduAnaliz'i Keşfet</span>
                                    </button>
                                </div>

                                {/* Social Proof Trust Line */}
                                <div className="pt-3 sm:pt-4 flex flex-wrap sm:flex-nowrap items-center justify-center lg:justify-start gap-3 sm:gap-4 border-t border-slate-200/80">
                                    <div className="flex -space-x-2 flex-shrink-0">
                                        <img className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover shadow-sm" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop" alt="Student" />
                                        <img className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover shadow-sm" src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop" alt="Student" />
                                        <img className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white object-cover shadow-sm" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="Student" />
                                    </div>
                                    <div className="text-[11px] sm:text-xs text-slate-600 text-left">
                                        <span className="font-extrabold text-[#0F2942]">15.000+ Öğrenci & Veli</span> tarafından aktif kullanılıyor
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: 3D Interactive Dashboard Card */}
                            <div className="lg:col-span-7 relative w-full mt-2 lg:mt-0">
                                
                                {/* Main Floating Dashboard Card */}
                                <div className="animate-float-dashboard relative bg-white/95 rounded-2xl sm:rounded-[32px] p-4 sm:p-6 md:p-7 shadow-xl sm:shadow-2xl shadow-slate-300/70 border border-slate-200/90 backdrop-blur-xl">
                                    
                                    {/* Dashboard Header */}
                                    <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-slate-100 mb-3.5 sm:mb-5 gap-2">
                                        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                                            <img src={branding.logoUrl || '/supervisor-logo-2026.png'} alt="Supervisor" className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-contain shadow-sm flex-shrink-0" />
                                            <div className="min-w-0">
                                                <h3 className="font-extrabold text-slate-800 text-xs sm:text-sm truncate">Öğrenci Kokpiti</h3>
                                                <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium truncate">YKS & LGS Canlı Performans Takibi</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dashboard Main Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5 sm:gap-5">
                                        
                                        {/* Left Sub-card: Net Gelişim Grafiği */}
                                        <div className="sm:col-span-7 bg-slate-50/90 rounded-xl sm:rounded-2xl p-3.5 sm:p-5 border border-slate-200/70">
                                            <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
                                                <div>
                                                    <h4 className="font-bold text-slate-800 text-xs">LGS & YKS Gelişim Grafiği</h4>
                                                    <p className="text-[10px] text-slate-500">Son 6 aylık deneme net artışı</p>
                                                </div>
                                                <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-blue-100 text-[#0095FF] text-[10px] sm:text-[11px] font-extrabold rounded-md shadow-sm whitespace-nowrap">+22.5 Net</span>
                                            </div>

                                            {/* SVG Net Curve */}
                                            <div className="relative h-32 sm:h-40 w-full pt-2">
                                                <svg className="w-full h-full overflow-visible" viewBox="0 0 300 120" preserveAspectRatio="none">
                                                    <defs>
                                                        <linearGradient id="chartGradientSvg" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#0095FF" stopOpacity="0.3" />
                                                            <stop offset="100%" stopColor="#0095FF" stopOpacity="0.0" />
                                                        </linearGradient>
                                                    </defs>
                                                    <line x1="0" y1="30" x2="300" y2="30" stroke="#E2E8F0" strokeDasharray="3 3" />
                                                    <line x1="0" y1="70" x2="300" y2="70" stroke="#E2E8F0" strokeDasharray="3 3" />
                                                    <line x1="0" y1="110" x2="300" y2="110" stroke="#E2E8F0" strokeDasharray="3 3" />
                                                    
                                                    <path d="M 0 100 Q 50 85, 100 65 T 200 45 T 300 15 L 300 120 L 0 120 Z" fill="url(#chartGradientSvg)" />
                                                    <path className="chart-animated-path" d="M 0 100 Q 50 85, 100 65 T 200 45 T 300 15" fill="none" stroke="#0095FF" strokeWidth="4" strokeLinecap="round" />
                                                    
                                                    <circle cx="0" cy="100" r="4" fill="#0F2942" />
                                                    <circle cx="100" cy="65" r="4" fill="#0F2942" />
                                                    <circle cx="200" cy="45" r="4" fill="#0F2942" />
                                                    <circle cx="300" cy="15" r="6.5" fill="#0095FF" stroke="#FFFFFF" strokeWidth="2.5" />
                                                </svg>

                                                <div className="absolute top-0 right-0 bg-[#0F2942] text-white text-[9px] sm:text-[11px] font-extrabold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md shadow-md">
                                                    115 Net (Hedef: 110)
                                                </div>
                                            </div>

                                            <div className="flex justify-between text-[10px] sm:text-[11px] font-bold text-slate-400 mt-2 px-1">
                                                <span>Eyl</span><span>Kas</span><span>Oca</span><span>Mar</span><span>May</span>
                                            </div>
                                        </div>

                                        {/* Right Sub-card: Günlük Hedefler (3 Circular Rings) */}
                                        <div className="sm:col-span-5 bg-slate-50/90 rounded-xl sm:rounded-2xl p-3.5 sm:p-5 border border-slate-200/70 flex flex-col justify-between">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-xs mb-0.5">Günlük Çalışma Hedefim</h4>
                                                <p className="text-[10px] text-slate-500 mb-2.5 sm:mb-3">Tamamlanan görevler</p>
                                            </div>

                                            {/* 3 Circles */}
                                            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                        <svg className="w-10 h-10 sm:w-12 sm:h-12 -rotate-90" viewBox="0 0 48 48">
                                                            <circle cx="24" cy="24" r="18" stroke="#E2E8F0" strokeWidth="3.5" fill="none" />
                                                            <circle cx="24" cy="24" r="18" stroke="#0095FF" strokeWidth="3.5" strokeDasharray="113" strokeDashoffset="39" strokeLinecap="round" fill="none" />
                                                        </svg>
                                                        <span className="absolute text-[10px] sm:text-[11px] font-extrabold text-[#0F2942]">65%</span>
                                                    </div>
                                                    <span className="text-[9px] sm:text-[10px] font-bold text-slate-700 mt-1">Türkçe</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                        <svg className="w-10 h-10 sm:w-12 sm:h-12 -rotate-90" viewBox="0 0 48 48">
                                                            <circle cx="24" cy="24" r="18" stroke="#E2E8F0" strokeWidth="3.5" fill="none" />
                                                            <circle cx="24" cy="24" r="18" stroke="#0F2942" strokeWidth="3.5" strokeDasharray="113" strokeDashoffset="33" strokeLinecap="round" fill="none" />
                                                        </svg>
                                                        <span className="absolute text-[10px] sm:text-[11px] font-extrabold text-[#0F2942]">70%</span>
                                                    </div>
                                                    <span className="text-[9px] sm:text-[10px] font-bold text-slate-700 mt-1">Matematik</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                        <svg className="w-10 h-10 sm:w-12 sm:h-12 -rotate-90" viewBox="0 0 48 48">
                                                            <circle cx="24" cy="24" r="18" stroke="#E2E8F0" strokeWidth="3.5" fill="none" />
                                                            <circle cx="24" cy="24" r="18" stroke="#0EA5E9" strokeWidth="3.5" strokeDasharray="113" strokeDashoffset="17" strokeLinecap="round" fill="none" />
                                                        </svg>
                                                        <span className="absolute text-[10px] sm:text-[11px] font-extrabold text-[#0F2942]">85%</span>
                                                    </div>
                                                    <span className="text-[9px] sm:text-[10px] font-bold text-slate-700 mt-1">Fen</span>
                                                </div>
                                            </div>

                                            <div className="mt-2.5 sm:mt-3.5 py-1 sm:py-1.5 px-2 bg-blue-50 rounded-xl text-center border border-blue-200/80 shadow-sm">
                                                <span className="text-[9px] sm:text-[11px] font-extrabold text-[#0095FF]">🔥 14 Günlük Çalışma Serisi</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bottom Row: AI Mentor & Student Review */}
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 mt-3.5 sm:mt-5">
                                        
                                        {/* AI Mentor */}
                                        <div className="sm:col-span-6 bg-blue-50/80 border border-blue-200/70 rounded-xl sm:rounded-2xl p-3 sm:p-3.5 flex items-start gap-2.5 sm:gap-3 shadow-sm">
                                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-[#0095FF] text-white flex items-center justify-center flex-shrink-0 text-xs sm:text-sm shadow-sm">
                                                🤖
                                            </div>
                                            <div className="text-xs min-w-0">
                                                <div className="font-bold text-[#0F2942] flex items-center gap-1.5">
                                                    <span className="text-xs">Yapay Zeka Mentoru</span>
                                                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                                                </div>
                                                <p className="text-slate-600 mt-1 leading-relaxed text-[10px] sm:text-[11px] font-medium">
                                                    <strong>Analiz Raporu:</strong> Matematik netlerin %20 arttı! Bugün 25 geometri pratiği yap.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Student Card: Esra K. */}
                                        <div className="sm:col-span-6 bg-white border border-slate-200 rounded-xl sm:rounded-2xl p-3 sm:p-3.5 shadow-sm flex items-center gap-2.5 sm:gap-3">
                                            <img className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-slate-100 shadow-sm flex-shrink-0" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&h=120&fit=crop" alt="Esra K." />
                                            <div className="text-xs overflow-hidden min-w-0">
                                                <div className="flex items-center justify-between gap-1">
                                                    <span className="font-extrabold text-slate-800 text-xs truncate">Esra K. <span className="text-[10px] text-slate-400 font-normal">(17 Yaş)</span></span>
                                                    <span className="text-amber-400 text-xs flex-shrink-0">★★★★★</span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 truncate mt-0.5 font-medium">"Supervisor ile hayalimdeki liseyi kazandım!"</p>
                                                <div className="text-[10px] font-extrabold text-emerald-600 mt-0.5">YKS Netleri: 98 Net ↗</div>
                                            </div>
                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>
                    </div>
                </section>

                {/* Grade Cards Section - Positions below Hero without overlap */}
                <div id="grade-programs" className="relative z-20 w-full overflow-hidden bg-white">
                    {/* Clean Subtle Supervisor Background Lighting */}
                    <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute top-0 right-0 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl pointer-events-none"></div>
                    
                    <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
                        <div className="text-center max-w-2xl mx-auto mb-14">
                            <span className="text-xs font-extrabold text-[#0095FF] uppercase tracking-wider bg-blue-50 px-3.5 py-1.5 rounded-full border border-blue-200/60">
                                Her Seviyeye Özel Programlar
                            </span>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F2942] mt-4 tracking-tight">
                                Sınav ve Okul Başarısı İçin Doğru Yol Haritası
                            </h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                            {/* Card 1 - Ortaokul (5-8. Sınıf) */}
                            <ScrollReveal delay={0} direction="left">
                                <div className="group flex flex-col bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/70 hover:shadow-2xl hover:shadow-blue-500/15 transition-all duration-500 hover:-translate-y-2.5 overflow-hidden border border-slate-200/80">
                                    <div className="relative h-72 overflow-hidden bg-slate-100">
                                        <img
                                            src="/landing-student.jpg.jpeg"
                                            loading="lazy"
                                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                                            alt="Ortaokul ve LGS Hazırlık"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2942]/85 via-[#0F2942]/25 to-transparent"></div>
                                        <div className="absolute bottom-6 left-7 flex items-center gap-4">
                                            <div className="w-13 h-13 px-3 py-2 bg-[#0095FF] rounded-2xl flex items-center justify-center text-white shadow-lg font-black text-center leading-tight">
                                                <span className="text-[11px] uppercase">5-8.<br />SINIF</span>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-sky-200 uppercase tracking-wide">LGS & Okul Desteği</span>
                                                <h3 className="text-2xl md:text-3xl font-extrabold text-white drop-shadow-md">
                                                    {content.gradeCards.card1.title || 'Ortaokul ve LGS'}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-8 md:p-10 bg-white flex flex-col flex-1">
                                        <p className="text-slate-600 text-base md:text-lg leading-relaxed font-medium mb-6">
                                            {content.gradeCards.card1.description || 'Yeni Maarif Modeline uygun konu kazanım testleri, beceri temelli LGS soruları, birebir özel dersler ve haftalık eğitim koçluğu.'}
                                        </p>
                                        <div className="mt-auto pt-4 flex items-center gap-2 text-[#0095FF] font-bold text-sm uppercase tracking-wider group-hover:gap-3.5 transition-all">
                                            <span>Programı İncele</span>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>

                            {/* Card 2 - Lise & Mezun (9-12 & Mezun) */}
                            <ScrollReveal delay={150} direction="right">
                                <div className="group flex flex-col bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/70 hover:shadow-2xl hover:shadow-blue-500/15 transition-all duration-500 hover:-translate-y-2.5 overflow-hidden border border-slate-200/80">
                                    <div className="relative h-72 overflow-hidden bg-slate-100">
                                        <img
                                            src="/uploads/landing/birebir-ders-premium.jpg"
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            alt="Lise ve YKS Hazırlık"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2942]/85 via-[#0F2942]/25 to-transparent"></div>
                                        <div className="absolute bottom-6 left-7 flex items-center gap-4">
                                            <div className="w-13 h-13 px-3 py-2 bg-[#0F2942] rounded-2xl flex items-center justify-center text-white shadow-lg font-black text-center leading-tight border border-blue-400/30">
                                                <span className="text-[11px] uppercase">9-12 &<br />MEZUN</span>
                                            </div>
                                            <div>
                                                <span className="text-xs font-bold text-sky-200 uppercase tracking-wide">YKS (TYT - AYT) & Kariyer</span>
                                                <h3 className="text-2xl md:text-3xl font-extrabold text-white drop-shadow-md">
                                                    {content.gradeCards.card2.title || 'Lise ve YKS Hazırlık'}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-8 md:p-10 bg-white flex flex-col flex-1">
                                        <p className="text-slate-600 text-base md:text-lg leading-relaxed font-medium mb-6">
                                            {content.gradeCards.card2.description || '9-12 ve mezunlar için TYT-AYT hazırlık dersleri, yapay zeka deneme karnesi analizleri, hedef üniversite koçluğu ve soru çözüm desteği.'}
                                        </p>
                                        <div className="mt-auto pt-4 flex items-center gap-2 text-[#0F2942] font-bold text-sm uppercase tracking-wider group-hover:gap-3.5 transition-all">
                                            <span>Programı İncele</span>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </div>

                {/* Marquee Section */}
                <div className="bg-slate-50 border-y border-slate-100 py-4 md:py-5 overflow-hidden relative mt-16 md:mt-32">
                    <div className="flex animate-marquee whitespace-nowrap">
                        {(() => {
                            const marqueeColors = [
                                'from-blue-400 via-cyan-400 to-blue-500',
                                'from-purple-400 via-pink-400 to-purple-500',
                                'from-emerald-400 via-teal-400 to-emerald-500',
                                'from-orange-400 via-amber-400 to-orange-500',
                                'from-red-400 via-rose-400 to-red-500'
                            ];
                            const texts = (content.marqueeTexts && content.marqueeTexts.length > 0)
                                ? content.marqueeTexts
                                : ['TYT - AYT', 'LGS HAZIRLIK', 'EĞİTİM KOÇLUĞU', 'TERCİH DANIŞMANLIĞI', 'YAPAY ZEKA'];

                            // Replicate for continuous scroll
                            return [...texts, ...texts, ...texts].map((text, idx) => {
                                const colorClass = marqueeColors[idx % marqueeColors.length];
                                return (
                                    <div key={idx} className="flex items-center mx-10">
                                        <span className={`bg-gradient-to-r ${colorClass} bg-clip-text text-transparent opacity-80 text-2xl font-black tracking-tighter uppercase italic pr-4`}>
                                            {text}
                                        </span>
                                        <span className="w-2 h-2 rounded-full bg-blue-500/30 mx-8"></span>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>

                {/* Testimonials Section */}
                <div id="testimonials" className="py-24 bg-white relative">
                    <div className="max-w-7xl mx-auto px-6">
                        <ScrollReveal direction="up">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">Bizi Tercih Eden Öğrenci ve Velilerimizin Yorumları</h2>
                            <p className="text-slate-600">
                                Başarıya giden yolda bizimle yürüyen öğrencilerin ve onlara destek olan velilerimizin deneyimleri.
                            </p>
                        </div>
                        </ScrollReveal>

                        <div className="grid md:grid-cols-2 gap-8">
                            <ScrollReveal delay={0} direction="left">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                                <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                                <p className="text-slate-600 leading-relaxed italic mb-6 flex-1">
                                    "Süpervisor Eğitim sayesinde çalışma disiplinimi oturttum. Yapay zeka destekli analizler eksiklerimi görmemi sağladı. Rehber hocamın desteği paha biçilemezdi."
                                </p>
                                <div>
                                    <h4 className="text-slate-900 font-bold">Zeynep Y.</h4>
                                    <span className="text-sm text-slate-500">12. Sınıf Öğrencisi</span>
                                </div>
                            </div>
                            </ScrollReveal>
                            
                            <ScrollReveal delay={150} direction="up">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                                <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                                <p className="text-slate-600 leading-relaxed italic mb-6 flex-1">
                                    "Çocuğumun deneme sonuçlarını ve gelişimini anlık olarak sistemden takip edebilmek bize büyük güven verdi. Emekleriniz için teşekkür ederiz."
                                </p>
                                <div>
                                    <h4 className="text-slate-900 font-bold">Ahmet K.</h4>
                                    <span className="text-sm text-slate-500">Öğrenci Velisi</span>
                                </div>
                            </div>
                            </ScrollReveal>
                            
                            <ScrollReveal delay={300} direction="up">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                                <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                                <p className="text-slate-600 leading-relaxed italic mb-6 flex-1">
                                    "Birebir dersler ve 7/24 soru sorabilme imkanı netlerimin artmasında çok etkili oldu. İyi ki yollarımız kesişmiş."
                                </p>
                                <div>
                                    <h4 className="text-slate-900 font-bold">Kerem M.</h4>
                                    <span className="text-sm text-slate-500">Mezun Öğrenci</span>
                                </div>
                            </div>
                            </ScrollReveal>
                            
                            <ScrollReveal delay={450} direction="right">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                                <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                                <p className="text-slate-600 leading-relaxed italic mb-6 flex-1">
                                    "Koçluk sistemi çocuğumun stres yönetiminde harikalar yarattı. Artık sınava daha özgüvenli hazırlanıyor."
                                </p>
                                <div>
                                    <h4 className="text-slate-900 font-bold">Ayşe T.</h4>
                                    <span className="text-sm text-slate-500">Öğrenci Velisi</span>
                                </div>
                            </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div id="features" className="bg-slate-50/50 py-24 relative overflow-hidden">
                    {/* Clean Ambient Background Glow */}
                    <div className="absolute top-20 left-10 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute top-40 right-20 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl pointer-events-none"></div>
                    
                    <div className="max-w-7xl mx-auto px-6 relative z-10">
                        <ScrollReveal direction="up">
                        <div className="text-center mb-16">
                            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wider mb-3">
                                Ayrıcalıklarımız
                            </p>
                            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
                                Potansiyelinizi{' '}
                                <span className="italic text-[#1e3a8a]">
                                    Tam Kapasite
                                </span>
                                {' '}Kullanın
                            </h2>
                            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                                Süpervisor Eğitim Ekosistemi, başarınız için ihtiyacınız olan tüm araçları tek bir platformda sunar.
                            </p>
                        </div>
                        </ScrollReveal>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                            {displayFeatures.map((feature, idx) => (
                                <ScrollReveal key={`reveal-${idx}`} delay={idx * 150} direction="up">
                                <div className="group relative bg-white/60 backdrop-blur-lg rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-700 hover:-translate-y-3 overflow-hidden border border-white flex flex-col h-full">
                                    {/* Image Section with Overlay */}
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        <img
                                            src={feature.imageUrl || 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=450&fit=crop'}
                                            alt={feature.title}
                                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-40 group-hover:opacity-20 transition-opacity duration-500"></div>

                                    </div>

                                    {/* Content Section */}
                                    <div className="px-8 pt-8 pb-8 flex flex-col h-full bg-white">
                                        <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-4 group-hover:text-blue-600 transition-colors uppercase tracking-tight leading-tight">
                                            {feature.title}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed font-bold text-sm md:text-base mb-6 flex-1">
                                            {feature.desc}
                                        </p>

                                        {/* Action Link */}
                                        <div className="flex items-center gap-2 text-blue-600 font-black text-xs md:text-sm uppercase tracking-widest group-hover:gap-4 transition-all opacity-0 group-hover:opacity-100">
                                            Detayları Keşfet
                                            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Pricing Section - Glassmorphism Redesign */}
                <div id="pricing" className="bg-slate-50 py-24">
                    <div className="max-w-7xl mx-auto px-6 text-center">
                        <ScrollReveal direction="up">
                        <p className="text-blue-600 text-sm font-semibold uppercase tracking-wider mb-3">
                            Size En Uygun Planı Seçin
                        </p>
                        <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-8">{content.pricing?.title}</h2>
                        </ScrollReveal>

                        {/* Period Selector */}
                        <div className="flex justify-center mb-16">
                            <div className="bg-white p-1.5 rounded-2xl shadow-lg border border-slate-100 flex gap-1">
                                {[
                                    { id: 'monthly' as const, ...content.pricing.billingCycles?.monthly },
                                    { id: '6months' as const, ...content.pricing.billingCycles?.sixMonths },
                                    { id: 'untilExam' as const, ...content.pricing.billingCycles?.untilExam }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setBillingCycle(tab.id as any)}
                                        className={`px-6 py-3 rounded-xl font-bold text-sm transition-all relative ${billingCycle === (tab.id === '6months' ? '6months' : tab.id)
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                                            }`}
                                    >
                                        {tab.label || (tab.id === 'monthly' ? 'Aylık' : tab.id === '6months' ? '6 Aylık' : 'Sınava Kadar')}
                                        {tab.badge && (
                                            <span className={`absolute -top-3 -right-2 px-2 py-0.5 rounded-full text-[10px] font-black ${billingCycle === (tab.id === '6months' ? '6months' : tab.id) ? 'bg-orange-500 text-white' : 'bg-blue-100 text-blue-600'
                                                }`}>
                                                {tab.badge}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-10">
                            {content.pricing?.packages.map((pkg, idx) => {
                                // Dynamic Price calculation based on billing cycle
                                let displayPrice = pkg.price;
                                let displayPeriod = pkg.period;

                                const basePriceStr = pkg.price.replace(/[^\d]/g, '');
                                const basePrice = parseInt(basePriceStr) || 0;

                                if (billingCycle === '6months' && content.pricing.billingCycles?.sixMonths) {
                                    const multiplier = content.pricing.billingCycles.sixMonths.multiplier;
                                    displayPrice = `${(basePrice * multiplier).toLocaleString('tr-TR')} ₺`;
                                    displayPeriod = content.pricing.billingCycles.sixMonths.periodLabel;
                                } else if (billingCycle === 'untilExam' && content.pricing.billingCycles?.untilExam) {
                                    const multiplier = content.pricing.billingCycles.untilExam.multiplier;
                                    displayPrice = `${(basePrice * multiplier).toLocaleString('tr-TR')} ₺`;
                                    displayPeriod = content.pricing.billingCycles.untilExam.periodLabel;
                                }

                                return (
                                    <div key={idx} className={`bg-white rounded-[3rem] p-10 shadow-xl transition-all hover:-translate-y-5 flex flex-col ${pkg.isPopular ? 'border-2 border-blue-600 scale-105 z-10' : 'border border-slate-100'}`}>
                                        <h3 className="text-2xl font-black mb-4">{pkg.name}</h3>
                                        <div className="mb-8">
                                            <span className="text-5xl font-black text-blue-600">{displayPrice}</span>
                                            <span className="text-slate-500 font-bold text-xl">{displayPeriod}</span>
                                        </div>
                                        <div className="space-y-4 mb-10 text-left flex-1">
                                            {pkg.features.map((f, fi) => (
                                                <div key={fi} className="flex items-center gap-3 font-bold text-slate-600">
                                                    <span className="text-green-500">✓</span>
                                                    <span className="text-sm">{f}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => onPlanClick?.(pkg.id)}
                                            className={`w-full py-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 ${pkg.isPopular
                                                ? 'bg-green-700 text-white shadow-xl hover:bg-green-600 shadow-green-500/20'
                                                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                                                }`}
                                        >
                                            {pkg.ctaText}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* About Supervisor Section */}
                <div id="about-supervisor" className="py-24 relative overflow-hidden bg-slate-50">
                    <div className="absolute inset-0 bg-blue-900/5"></div>
                    <div className="max-w-7xl mx-auto px-6 relative z-10">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <ScrollReveal direction="left">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-sky-400/20 rounded-2xl blur-2xl"></div>
                                <img
                                    src="/landing-teacher.jpg.jpeg"
                                    alt="Supervisor Eğitim Ekosistemi"
                                    className="relative rounded-2xl shadow-2xl border border-slate-200 object-cover w-full aspect-[4/3]"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl border border-slate-100 shadow-xl">
                                    <div className="text-3xl font-extrabold text-[#0F2942] mb-0.5">AI</div>
                                    <div className="text-xs font-bold text-[#0095FF]">Destekli Öğrenme</div>
                                </div>
                            </div>
                            </ScrollReveal>
                            <ScrollReveal direction="right" delay={200}>
                            <div>
                                <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                    {content?.aboutSupervisor?.title || 'Eğitimde Yeni Bir Dönem'}
                                </h2>
                                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                                    {content?.aboutSupervisor?.description || 'Supervisor Eğitim, sadece bir ders çalışma platformu değil, öğrencinin akademik ve kişisel yolculuğunda ona eşlik eden akıllı bir yol arkadaşıdır.'}
                                </p>
                                <div className="space-y-4">
                                    {(content?.aboutSupervisor?.points && content.aboutSupervisor.points.length > 0) ? (
                                        content.aboutSupervisor.points.map((point, idx) => (
                                            <div key={idx} className="flex items-start gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                                    <span className="text-blue-400 font-bold">{point.number}</span>
                                                </div>
                                                <div>
                                                    <h4 className="text-slate-900 font-bold mb-2">{point.title}</h4>
                                                    <p className="text-sm text-slate-600">{point.description}</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            <div className="flex items-start gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                                    <span className="text-blue-400 font-bold">01</span>
                                                </div>
                                                <div>
                                                    <h3>Bütüncül Yaklaşım</h3>
                                                    <p className="text-sm text-slate-600">Akademik başarıyı sosyal ve kişisel gelişimle birleştiriyoruz.</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                                                    <span className="text-purple-400 font-bold">02</span>
                                                </div>
                                                <div>
                                                    <h4 className="text-slate-900 font-bold mb-2">Veri Odaklı Takip</h4>
                                                    <p className="text-sm text-slate-600">Her adımı verilerle analiz edip en doğru yönlendirmeyi yapıyoruz.</p>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </div >

                {/* About Us Section */}
                <div id="about-us" className="py-24 bg-white relative">
                    <div className="max-w-7xl mx-auto px-6">
                        <ScrollReveal direction="up">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">Vizyonumuz & Misyonumuz</h2>
                            <p className="text-slate-600">
                                Geleceğin liderlerini yetiştirmek için çıktığımız bu yolda, teknolojinin gücünü eğitimin hizmetine sunuyoruz.
                            </p>
                        </div>
                        </ScrollReveal>

                        <div className="grid md:grid-cols-2 gap-8">
                            <ScrollReveal delay={0} direction="left">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-500/30 transition-all group shadow-sm hover:shadow-xl">
                                <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                                        <circle cx="12" cy="12" r="6" strokeWidth="1.5" />
                                        <circle cx="12" cy="12" r="2" strokeWidth="1.5" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">{content?.visionMission?.mission?.title || 'Misyonumuz'}</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    {content?.visionMission?.mission?.description || 'Her öğrencinin potansiyelini keşfetmesine ve gerçekleştirmesine yardımcı olmak. Bireyselleştirilmiş eğitim deneyimi ile akademik ve kariyer hedeflerine ulaşmalarını sağlamak, fırsat eşitliği yaratarak kaliteli eğitime erişimi kolaylaştırmak.'}
                                </p>
                            </div>
                            </ScrollReveal>

                            <ScrollReveal delay={200} direction="right">
                            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-purple-500/30 transition-all group shadow-sm hover:shadow-xl">
                                <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                    <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">{content?.visionMission?.vision?.title || 'Vizyonumuz'}</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    {content?.visionMission?.vision?.description || "Türkiye'nin en kapsamlı ve yenilikçi eğitim teknolojisi platformu olarak, yapay zeka destekli kişiselleştirilmiş öğrenme deneyimi sunarak eğitimde standartları yükseltmek ve global ölçekte başarı hikayeleri oluşturmak."}
                                </p>
                            </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </div >

                {/* Stats Section */}
                <div id="stats" className="py-20 bg-white border-y border-slate-100 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5"></div>
                    <div className="max-w-7xl mx-auto px-6 relative z-10">
                        <div id="stats-section" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 text-center">
                            {(content?.stats && content.stats.length > 0) ? (
                                content.stats.map((stat, idx) => (
                                    <div key={idx} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 group hover:bg-white hover:shadow-xl transition-all">
                                        <div className={`text-3xl lg:text-4xl font-bold bg-gradient-to-r ${stat.color || 'from-blue-600 to-cyan-600'} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform`}>
                                            <AnimatedCounter end={parseInt(stat.value)} suffix={stat.suffix} />
                                        </div>
                                        <div className="text-slate-600 font-medium uppercase text-xs tracking-widest">{stat.label}</div>
                                    </div>
                                ))
                            ) : (
                                // Fallback defaults
                                [
                                    { val: 1000, suf: 'k+', lab: 'Aktif Öğrenci', col: 'from-blue-400 to-cyan-400' },
                                    { val: 100, suf: '+', lab: 'Uzman Eğitim Danışmanı', col: 'from-purple-400 to-pink-400' },
                                    { val: 350, suf: '+', lab: 'Uzman Öğretmen', col: 'from-red-400 to-orange-400' },
                                    { val: 86, suf: '%', lab: 'Akademik Başarıda Artış', col: 'from-emerald-400 to-teal-400' },
                                    { val: 98, suf: '%', lab: 'Öğrenci Memnuniyeti', col: 'from-orange-400 to-amber-400' }
                                ].map((stat, idx) => (
                                    <div key={idx} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 group hover:bg-white hover:shadow-xl transition-all">
                                        <div className={`text-3xl lg:text-4xl font-bold bg-gradient-to-r ${stat.col.replace('400', '600')} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform`}>
                                            <AnimatedCounter end={stat.val} suffix={stat.suf} />
                                        </div>
                                        <div className="text-slate-600 font-medium uppercase text-xs tracking-widest">{stat.lab}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div >

                {/* CTA Section */}
                < div id="cta" className="py-24 relative overflow-hidden" >
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-white"></div>
                    <ScrollReveal direction="up" duration={900}>
                    <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                            {content?.cta?.title || `Geleceğini Şansa Bırakma, ${branding.companyName} ile Planla`}
                        </h2>
                        <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
                            {content?.cta?.description || 'Hemen ücretsiz deneme hesabınızı oluşturun, yapay zeka destekli eğitim koçunuzla tanışın.'}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => setIsContactModalOpen(true)}
                                className="px-8 py-4 bg-green-700 text-white font-bold rounded-xl hover:bg-green-600 hover:scale-105 active:scale-95 transition-all shadow-lg shadow-green-500/25 w-full sm:w-auto text-lg flex items-center justify-center gap-2"
                            >
                                {content?.cta?.primaryButtonText || 'Sizi Arayalım'}
                                <PhoneIcon className="w-6 h-6" />
                            </button>
                            <button className="px-8 py-4 bg-slate-100 text-slate-900 font-bold rounded-xl hover:bg-slate-200 transition-all border border-slate-200 w-full sm:w-auto text-lg">
                                {content?.cta?.secondaryButtonText || 'Danışman İle Görüş'}
                            </button>
                        </div>
                    </div>
                    </ScrollReveal>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-slate-50 border-t border-slate-100 pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <img
                                    src={branding.logoUrl}
                                    alt={branding.companyName}
                                    className="h-10 md:h-12 rounded-xl shadow-md object-contain"
                                />
                            </div>
                            <p className="text-gray-500 leading-relaxed mb-6">
                                {footerContent.description}
                            </p>
                            <div className="flex gap-4">
                                {Object.entries(footerContent.socialLinks)
                                    .filter(([_, url]) => url)
                                    .map(([social, url]) => (
                                        <a key={social} href={url || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-sm">
                                            <span className="capitalize">{social[0]}</span>
                                        </a>
                                    ))}
                                {/* Show default icons if no social links */}
                                {Object.values(footerContent.socialLinks).every(url => !url) &&
                                    ['twitter', 'instagram', 'linkedin', 'youtube'].map((social) => (
                                        <a key={social} href="#" className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-sm">
                                            <span className="capitalize">{social[0]}</span>
                                        </a>
                                    ))
                                }
                            </div>
                        </div>

                        <div>
                            <h3>Platform</h3>
                            <ul className="space-y-4 text-slate-600">
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Nasıl Çalışır?</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Özellikler</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Fiyatlandırma</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">SSS</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-slate-800 font-bold mb-6 text-lg">Kurumsal</h4>
                            <ul className="space-y-4 text-slate-600">
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Hakkımızda</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Kariyer</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">Blog</a></li>
                                <li><a href="#" className="hover:text-blue-600 transition-colors">İletişim</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-slate-800 font-bold mb-6 text-lg">Yasal</h4>
                            <ul className="space-y-4 text-slate-600">
                                <li><a href="/terms" className="hover:text-blue-600 transition-colors font-semibold">Kullanım Koşulları</a></li>
                                <li><a href="/privacy" className="hover:text-blue-600 transition-colors font-semibold">Gizlilik Politikası</a></li>
                                <li><a href="/privacy" className="hover:text-blue-600 transition-colors">KVKK</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-slate-200/60 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-gray-500 text-sm">
                            &copy; 2026 {branding.companyName}. Tüm hakları saklıdır.
                        </p>
                    </div>
                </div>
            </footer>

            <ContactFormModal
                isOpen={isContactModalOpen}
                onClose={() => {
                    setIsContactModalOpen(false);
                    setSelectedGrade('');
                }}
                selectedPackage={selectedGrade}
            />

            {/* Custom Styles */}
            <style> {`
    @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
    }
    .animate-scroll,.animate-marquee {
    animation: scroll 20s linear infinite;
    }
    @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    }
    @keyframes float-delayed {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
    }
    @keyframes float-slow {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    50% { transform: translateY(-5px) translateX(2px); }
    }
    .animate-float {
    animation: float 3s ease-in-out infinite;
    }
    .animate-float-delayed {
    animation: float-delayed 3.5s ease-in-out infinite 0.5s;
    }
    .animate-float-slow {
    animation: float-slow 4s ease-in-out infinite;
    }
    @keyframes blob {
      0% { transform: translate(0px, 0px) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    .animate-blob {
      animation: blob 7s infinite;
    }
    .animation-delay-2000 {
      animation-delay: 2s;
    }
    .animation-delay-4000 {
      animation-delay: 4s;
    }
`}</style>
        </div>
    );
};
